import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { I18n } from 'aws-amplify'
import UserContext from './../../app/UserContext';
import { getUsers } from './api/User'

import { ConstantEquals, USER, SORT_DIRECTION } from './../../shared/constants/index'
import { isObject, isNull } from './../../shared/utils/misc'

import { Container, Row, Col, Table, Button, Spinner } from 'react-bootstrap'
import { SortDown, SortUp } from 'react-bootstrap-icons';

import { UserFilter } from './UserFilter'
import { UserRecord } from './UserRecord'

export const UserList = props => {

  const PAGE_SIZE = 500;

  const { user } = useContext(UserContext);

  // 非同期制御：ユーザ取得
  const [userLoad, setUserLoad] = useState(false)
  // Data: ユーザリスト
  const [userData, setUserData] = useState([])

  const [userFilter, setUserFilter] = useState({'active': true})
  const [userSort, setUserSort] = useState({
    'key': USER.TABLE_COLUMN.UPDATED_AT,
    'direction': SORT_DIRECTION.DESC
  })
  const [userNextToken, setUserNextToken] = useState(null)
  // UI: 非同期制御
  const [userNextRead,  setUserNextRead] = useState(false)

  // 初回マウント時
  useEffect(() => {
    // Stateのメモリリーク対策
    let isMounted = true;
    // 取得
    (async() => {

      try{
        if (isMounted) setUserLoad(true)

        const res = await getUsers(user.username, userFilter, null, PAGE_SIZE);
        // 取得した情報をparseする
        const parseData = parseUser(res?.data?.listUserByUpdatedAt)


        // 描画
        if (isMounted){
          setUserData(parseData.items)
          setUserNextToken(parseData.nextToken)
          setUserNextRead(false)
          setUserLoad(false)
        }

      }
      catch(err){
        console.log(err)
        if (isMounted) setUserLoad(false);
      }

    })();
    return () => { 
      // マウントされていない場合はフラグをクリーンにする（setStateさせない）
      isMounted = false 
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 検索実行
  useEffect(() => {
    // 検索用取得
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter])

  
  // 件数の取得（1ページ目）
  const fetchUsers = async() => {
    setUserLoad(true)
    setUserData([]);

    try{
      const res = await getUsers(user.username, userFilter, null, PAGE_SIZE);

      // 取得した情報をparseする
      const parsed = parseUser(res?.data?.listUserByUpdatedAt)
      setUserData(parsed.items);
      setUserNextToken(parsed.nextToken);
      setUserNextRead(false);
    }
    catch (err) {
      console.log(err);
    } finally {
      setUserLoad(false);
    }
  }

  //  追加読み込み
  const addFetchUsers = async() => {
    setUserNextRead(true)

    try {
      const res = await getUsers(user.username, userFilter, userNextToken, PAGE_SIZE);
      const parsed = parseUser(res?.data?.listUserByUpdatedAt);
      setUserData(prev => [...prev, ...parsed.items]);
      setUserNextToken(parsed.nextToken);
    } catch (err) {
      console.log(err);
    } finally {
      setUserNextRead(false);
    }
  }

  // 取得したユーザデータのパース
  const parseUser = (users) =>{

    const out = { done: true, items: [], nextToken: null };
    if (!users || !users.items || users.items.length === 0) return out;

    out.items = users.items.map((value) => {
      const model = {};
      Object.entries(value).forEach(([key, val]) => {
        if (key === USER.LABEL.ID) model[USER.TABLE_COLUMN.ID] = val;
        if (key === USER.LABEL.USERNAME) model[USER.TABLE_COLUMN.USERNAME] = val;
        if (key === USER.LABEL.COMPANY_NAME) model[USER.TABLE_COLUMN.COMPANY_NAME] = val;
        if (key === USER.LABEL.MAILADDRESS) model[USER.TABLE_COLUMN.MAILADDRESS] = val;
        if (key === USER.LABEL.ACTIVE) model[USER.TABLE_COLUMN.ACTIVE] = val;
        if (key === USER.LABEL.UPDATED_AT) model[USER.TABLE_COLUMN.UPDATED_AT] = val;
        if (key === USER.LABEL.CREATED_AT) model[USER.TABLE_COLUMN.CREATED_AT] = val;
        if (key === USER.LABEL.LICENCE) {
          const count = Array.isArray(val?.items) ? val.items.length : 0;
          model[USER.TABLE_COLUMN.LICENCE_NUM] = count;
        }
        if (key === USER.LABEL.CORP) model[USER.TABLE_COLUMN.CORP] = val;
      });
      return model;
    });

    out.nextToken = users.nextToken ?? null;
    return out;
  }

  // ソート
  const userSortable = (a, b) => {
    const key = userSort.key;
    const dir = userSort.direction;

    const av = a[key];
    const bv = b[key];

    // null/undefined は末尾へ
    if (av == null && bv == null) return (a.id || '').localeCompare(b.id || '');
    if (av == null) return dir === SORT_DIRECTION.ASC ? 1 : -1;
    if (bv == null) return dir === SORT_DIRECTION.ASC ? -1 : 1;

    // 数値列（ライセンス数など）
    if (key === USER.TABLE_COLUMN.LICENCE_NUM) {
      const an = Number(av) || 0;
      const bn = Number(bv) || 0;
      if (an === bn) return (a.id || '').localeCompare(b.id || '');
      return dir === SORT_DIRECTION.ASC ? (an - bn) : (bn - an);
    }

    // 文字列/ISO日付（文字列比較でOK）
    const as = typeof av === 'string' ? av : String(av);
    const bs = typeof bv === 'string' ? bv : String(bv);
    const cmp = as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' });
    if (cmp !== 0) return dir === SORT_DIRECTION.ASC ? cmp : -cmp;

    // タイブレーク
    return (a.id || '').localeCompare(b.id || '');
  };

  // 表示直前にローカル並べ替え
  const sortedData = useMemo(() => {
    const arr = Array.isArray(userData) ? [...userData] : [];
    arr.sort(userSortable);
    return arr;
  }, [userData, userSort]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ソート切替（再フェッチしない）
  const changeUserSort = (key, direction) => {
    setUserSort({ key, direction });
  };

  // ソートアイコンを返す
  const resSortIcon = (argKey) => {
    if (userSort.key === argKey) {
      return userSort.direction === SORT_DIRECTION.ASC
        ? <SortUp style={{ cursor: 'pointer' }} size={12} onClick={() => changeUserSort(argKey, SORT_DIRECTION.DESC)} />
        : <SortDown style={{ cursor: 'pointer' }} size={12} onClick={() => changeUserSort(argKey, SORT_DIRECTION.ASC)} />;
    }
    return <SortDown style={{ cursor: 'pointer' }} color="gray" size={12} onClick={() => changeUserSort(argKey, SORT_DIRECTION.DESC)} />;
  };

  const noData = !Array.isArray(sortedData) || sortedData.length === 0;

  return(
    <>
      {/* User List */}
      <Container className="main-content">

        <Row >
          <Col xs={8} md={8} >
            <h2>{I18n.get('title_user_list')}</h2>
          </Col>
          <Col xs={4} md={4} >
            <div className="text-end" >
              <Link to='/user' >
                <Button variant="primary" type="submit">
                  {I18n.get('title_user_add')}
                </Button>
              </Link>
            </div>
          </Col>
        </Row>

        <UserFilter
          filter={setUserFilter}
          nextToken={setUserNextToken}
          nextRead={setUserNextRead}
          load={userLoad}
        />

        <Row >
          <Col xs={12} md={12} className="mt-3">

          {userLoad ? (
            <div className="text-center">
              <Spinner
                as="span"
                animation="border"
                role="status"
                aria-hidden="true"
              />
            </div>
          ) : noData ? (
            // データなし
            <>{I18n.get('general_no_data')}</>
          ):(
            <Table striped bordered hover responsive className="text-nowrap">
              <thead>
                <tr>
                  <th>{I18n.get('general_status')}{` `}{resSortIcon(USER.TABLE_COLUMN.ACTIVE)}</th>
                  <th>{I18n.get('table_column_user_username')}{` `}{resSortIcon(USER.TABLE_COLUMN.USERNAME)}</th>
                  <th>{I18n.get('table_column_user_company_name')}{` `}{resSortIcon(USER.TABLE_COLUMN.COMPANY_NAME)}</th>
                  <th>{I18n.get('table_column_user_mailaddress')}{` `}{resSortIcon(USER.TABLE_COLUMN.MAILADDRESS)}</th>
                  <th>{I18n.get('table_column_user_licence_num')}{` `}{resSortIcon(USER.TABLE_COLUMN.LICENCE_NUM)}</th>
                  <th>{I18n.get('general_latest_update')}{` `}{resSortIcon(USER.TABLE_COLUMN.UPDATED_AT)}</th>
                  <th>{I18n.get('general_create_date')}{` `}{resSortIcon(USER.TABLE_COLUMN.CREATED_AT)}</th>
                </tr>
              </thead>
              <tbody>
                <>
                  { sortedData.map((cUser, index) => (
                    <UserRecord key={cUser.id ?? `ur-${index}`} user={cUser} index={index} />
                  ))}
                </>
              </tbody>
              { userNextToken ? (
                <tfoot>
                  <tr>
                    { userNextRead ? ( 
                      <td colSpan={8} className="text-center">
                        <Spinner
                          as="span"
                          animation="border"
                          role="status"
                          aria-hidden="true" />
                      </td>
                    ): (
                      <td colSpan={8} className="text-center styleLiceRecd"
                        onClick={() => addFetchUsers()} >
                        {I18n.get('general_paging')}
                      </td>
                    ) }
                  </tr>
                </tfoot>
              ) : (<></>)}
            </Table>
          )}
          </Col>
        </Row>
      </Container>
    </>
  );
}